import React, { useEffect, useRef ,useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { FaPlus } from "react-icons/fa6";
import { Button, Tooltip, Space ,message} from "antd";
import { MdOutlineTipsAndUpdates } from "react-icons/md";



/**
 * Props:
 * - initialHtml?: string
 * - onExport?: (fileOrBlob: File|Blob, text: string, filename: string) => void
 */
export default function PhysioBuzzEditor({ initialHtml = "", onExport }) {
  const editorRef = useRef(null);
 const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 750;

  const withEditor = (fn) => {
    const ed = editorRef.current;
    if (!ed) return;
    fn(ed);
  };

  useEffect(() => {
    const ed = editorRef.current;
    if (ed && typeof initialHtml === "string") {
      ed.setContent(initialHtml);
    }
  }, [initialHtml]);

  // ✅ Character count
  const handleEditorChange = (content) => {
    const plainText = content
      .replace(/<[^>]*>/g, "") // strip HTML tags
      .replace(/&nbsp;/g, " ") // replace nbsp
      .trim();
    setCharCount(plainText.length);
  }

  const wrapWithTag = (tag) =>
    withEditor((ed) => {
      const html = ed.selection.getContent({ format: "html" }) || "";
      if (!html) return;

      // If selection already has STEP/STEPNI, keep only the requested tag
      if (/\[(?:STEP|STEPNI)\]/i.test(html)) {
        const cleaned = html.replace(/\[\/?(STEP|STEPNI)\]/gi, "");
        ed.selection.setContent(`[${tag}]${cleaned}[${tag}]`);
        return;
      }

      // Symmetric markers (what your pipeline expects): [STEP] ... [STEP]
      ed.selection.setContent(`[${tag}]${html}[${tag}]`);
    });

  const addCustomBullets = () =>
    withEditor((ed) => {
      const selectedText = ed.selection.getContent({ format: "text" });
      const lines = selectedText.split("\n");
      const bulleted = lines
        .map((line) => (line.trim() ? "• " + line.trim() : ""))
        .join("<br>");
      ed.selection.setContent(bulleted);
    });

  const adjustLineHeightExternal = (delta) =>
    withEditor((ed) => {
      const blocks = ed.selection.getSelectedBlocks();
      blocks.forEach((block) => {
        let style = block.getAttribute("style") || "";
        const match = style.match(/line-height\s*:\s*([\d.]+)/);
        let current = match ? parseFloat(match[1]) : 1.5;
        let updated = Math.max(1, current + delta);
        style = style.replace(/line-height\s*:\s*[\d.]+;?/gi, "").trim();
        style += (style.length ? "; " : "") + `line-height: ${updated};`;
        block.setAttribute("style", style);
      });
      ed.nodeChanged();
    });

  // replace your current exportToTxt with this one
  const exportToTxt = () =>
    withEditor((ed) => {
        if (charCount > MAX_CHARS) {
        message.error(`Character limit exceeded! Max ${MAX_CHARS} allowed.`);
        return;
      }
      let content = ed.getContent({ format: "html" });

      // Convert <li> to bullets and strip list wrappers
      content = content.replace(
        /<li[^>]*>(.*?)<\/li>/gis,
        (_, inner) => `• ${inner}<br>`
      );
      content = content.replace(/<\/?(ul|ol)[^>]*>/gi, "");

      // Remove obvious empty <p> blocks (pure whitespace/nbsp/br/span shells)
      content = content.replace(
        /<p[^>]*>(\s|&nbsp;|<br\s*\/?>|(<span[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/span>))*<\/p>/gi,
        ""
      );

      // Preserve ANY leading/trailing marker sequences verbatim (including duplicates)
      const leadSeqMatch = content.match(/^(\s*(?:\[(?:STEP|STEPNI)\]\s*)+)/i);
      const trailSeqMatch = content.match(
        /((?:\s*\[(?:STEP|STEPNI)\]\s*)+)\s*$/i
      );
      const leadingMarkers = leadSeqMatch ? leadSeqMatch[1] : "";
      const trailingMarkers = trailSeqMatch ? trailSeqMatch[1] : "";

      // Remove those sequences before parsing paragraphs
      if (leadingMarkers)
        content = content.replace(/^(\s*(?:\[(?:STEP|STEPNI)\]\s*)+)/i, "");
      if (trailingMarkers)
        content = content.replace(/((?:\s*\[(?:STEP|STEPNI)\]\s*)+)\s*$/i, "");



      // Split into paragraphs
// Split into blocks: <p> and <h1>.. <h6>
const blockMatches =
  content.match(/<(p|h[1-6])[^>]*>[\s\S]*?<\/\1>/gi) || [];


const isMeaningful = (html) => {
  const textish = html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")      // strip tags
    .replace(/&nbsp;/gi, " ")
    .trim();
  return textish.length > 0;
};


const paras = blockMatches
  .map((blockHtml) => {
    // detect inline STEP/STEPNI
    const tagMatch = blockHtml.match(/\[(STEP|STEPNI)\]/i);
    const tag = tagMatch ? tagMatch[1].toUpperCase() : null;

    // remove STEP/STEPNI markers but keep <p> / <h1>.. tags as-is
    const cleanHtml = blockHtml
      .replace(/\[\/?(STEP|STEPNI)\]/gi, "")
      .trim();

    return { tag, html: cleanHtml };
  })
  .filter(({ html }) => isMeaningful(html));


const SEP = "  "; // two spaces between blocks
const blocks = paras.map(
  ({ tag, html }) => `[${tag || "STEP"}]${html}[${tag || "STEP"}]`
);

const finalOutput = `${leadingMarkers}${blocks.join(
  SEP
)}${trailingMarkers}`;




      const filename = `chapter_slide_${Date.now()}.txt`;
      if (typeof onExport === "function") {
        let file;
        try {
          file = new File([finalOutput], filename, { type: "text/plain" });
        } catch {
          file = new Blob([finalOutput], { type: "text/plain" });
          file.name = filename;
        }
        onExport(file, finalOutput, filename);
      }
    });

  return (
    <div className="page">
      <h2> Physio Buzz Editor</h2>

      <div className="editor-container">
        <div className="custom-controls">
          <Space size="small" wrap>
            <Tooltip
              placement="bottom"
              title="Use this tag to highlight a portion of your content that will appear in the app’s “on-tap preview”. It’s perfect for showing important snippets in an interactive way."
            >
              <Button
                size="small"
                onClick={() => wrapWithTag("STEP")}
                // title="Insert [STEP] tag"
              >
                [STEP]
              </Button>
            </Tooltip>

            <Tooltip
              placement="bottom"
              title="This tag works like [STEP], but it’s designed for images. When applied, the image will remain hidden. This gives you better control over what users see on their screen while keeping the layout clean and engaging."
            >
              <Button
                size="small"
                onClick={() => wrapWithTag("STEPNI")}
                // title="Insert [STEPNI] tag"
              >
                [STEPNI]
              </Button>
            </Tooltip>

            <Tooltip
              placement="bottom"
              title="Add bullet to each selected line"
            >
              <Button
                size="small"
                onClick={addCustomBullets}
                title="Add bullet to each selected line"
              >
                • Bullet
              </Button>
            </Tooltip>

            <Tooltip placement="bottom" title="Increase line height">
              <Button
                size="small"
                onClick={() => adjustLineHeightExternal(0.2)}
                title="Increase line height"
              >
                Line Spacing +
              </Button>
            </Tooltip>

            <Tooltip placement="bottom" title="Decrease line height">
              <Button
                size="small"
                onClick={() => adjustLineHeightExternal(-0.2)}
                title="Decrease line height"
              >
                Line Spacing -
              </Button>
            </Tooltip>
          </Space>
        </div>

          <div style={{ color: "#64748b", fontSize: 13 }} className="tip-text">
                    <MdOutlineTipsAndUpdates />  Tip: First, add your content. Then, select the [STEP] or [STEPNI] tags, and finally, format the text as you want using the editor tools.
                  </div>



        <Editor
          onInit={(_, editor) => {
            editorRef.current = editor;
            if (initialHtml) editor.setContent(initialHtml);
          }}
           onEditorChange={handleEditorChange}
          tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@5.10.7/tinymce.min.js"
          init={{
            height: 500,
            menubar: "file edit format view insert",
            plugins: "lists link image code textcolor advlist",
            toolbar:
              "undo redo | formatselect | fontsizeselect fontselect | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify | outdent indent | code",
            font_formats:
              "Open Sans=Open Sans,Arial,sans-serif;DauphinPlain=DauphinPlain;Arial=arial,helvetica,sans-serif;Courier New=courier new,courier;Georgia=georgia,palatino;",
            fontsize_formats:
              "10px 12px 14px 16px 18px 24px 36px 54px 64px 80px 100px",
            content_style:
              "body { font-family: Open Sans, Arial, sans-serif; font-size:14px; }",
          }}
        />
                             {/* ✅ Word counter */}
        <div
          style={{
            textAlign: "right",
            fontSize: 13,
            marginTop: 5,
            color: charCount > MAX_CHARS ? "red" : "#64748b",
          }}
        >
          {charCount}/{MAX_CHARS} characters
        </div>

        <button className="export-btn" onClick={exportToTxt} disabled={charCount > MAX_CHARS}>
          <FaPlus /> Generate Content
        </button>
      </div>

      <div className="branding">Powered by Physio Buzz</div>

      <style>{`
        :root { --bg-color:#FCFAFC; --primary-color:#532547; --accent-color:#CC99CC; --text-color:#333; }
        * { box-sizing: border-box; }
        .page { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin:0; padding:20px; background:var(--bg-color); color:var(--text-color); }
        h2 { text-align:center; color:var(--primary-color); margin-bottom:20px; font-size:28px; position:relative; }
        h2::after { content:''; display:block; width:100px; height:4px; margin:10px auto 0; background:var(--accent-color); border-radius:2px; }
        .editor-container { display:flex; flex-direction:column; gap:20px; max-width:1000px; margin:auto; padding:20px; background:#fff; border-radius:12px; box-shadow:0 8px 20px rgba(0,0,0,0.06); }
        .custom-controls { display:flex; flex-wrap:wrap; gap:10px; justify-content:center; margin-bottom:10px; }
        .custom-controls button, .export-btn { padding:10px 18px; background-color:var(--primary-color); color:#fff; border:none; border-radius:6px; cursor:pointer; font-size:14px; transition:all .3s ease; }
        .custom-controls button:hover, .export-btn:hover { background-color:#3d1938; }
        .export-btn { display: flex;align-items: center; justify-content: center; gap: 5px;}
        .branding { text-align:center; font-size:16px; color:var(--accent-color); margin-top:40px; font-style:italic; }
        @media (max-width:768px){ h2{font-size:22px;} .editor-container{padding:15px;} .custom-controls{flex-direction:column; align-items:stretch;} .custom-controls button{width:100%;} }
      `}</style>
    </div>
  );
}
